<script lang="ts">
import type { CheckboxGroupItem, CheckboxGroupProps } from '@nuxt/ui';
import type { SelectFieldOptions } from '#layers/block-engine/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { defineBlock, selectOptionsFormSchema, useBlockInEditorContext, useSelectOptions } from '#layers/block-engine/public';

interface InputCheckboxBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<CheckboxGroupProps, 'size' | 'variant' | 'color' | 'icon'> {
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_checkbox.name',
	description: '$t:blocks.input_checkbox.description',
	icon: 'lucide:square-check',
	category: 'input',
	fieldBinding: { supportedTypes: ({ typeOptions }) => typeOptions?.multiple ? ['select'] : [] },

	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'multiple',
			label: '$t:blocks.common.props.multiple',
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'icon',
			label: '$t:blocks.common.props.icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
// TODO: Resolve circular dependency with `#layers/data-model` during built-in block metadata initialization.
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

const {
	options,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	size,
	variant,
	color,
} = defineProps<InputCheckboxBlockProps>();
const model = defineModel<any>();

;

const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
// reka's checkbox group silently drops toggles when its model is nullish; keep it an array.
const selected = computed({
	get: () => Array.isArray(modelValue.value) ? modelValue.value : [],
	set: (value) => {
		modelValue.value = value;
	},
});
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const forwarded = computed(() => ({ size, variant, color }));

const { normalizedOptions: items } = useSelectOptions<CheckboxGroupItem>(() => options ?? []);

const { isPreview } = useBlockInEditorContext();
const previewItems = computed<CheckboxGroupItem[]>(() => Array.from({ length: 3 }, (_, i) => ({
	value: `option-${i + 1}`,
	label: `Option ${i + 1}`,
}) satisfies CheckboxGroupItem));
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DCheckboxGroup
			v-bind="forwarded"
			v-model="selected"
			:disabled
			:aria-readonly="readonly"
			:items="isPreview ? previewItems : items"
			:ui="{
				item: readonly ? 'pointer-events-none' : '',
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
